import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAffiliate } from '../../api/affiliates/useAffiliate';
import { useReadUserIdFromQueryParams } from '../../hooks/useReadUserIdFromQueryParams';
import { useScheduleAppointment } from '../../api/scheduleAppointment/useScheduleAppointment';
import { useFetchAvailability } from './useFetchAvailability';
import { useTracking } from '../../hooks/useTracking';
import {
  TRACKING_CLICKED_CTA_TO_BOOK_APPOINTMENT,
  TRACKING_ERROR_ON_BOOK_APPOINTMENT,
  TRACKING_SELECT_APPOINTMENT_DATE,
  TRACKING_SELECT_APPOINTMENT_TIME,
  TRACKING_SKIP_APPOINTMENT_SCHEDULE,
  TRACKING_SUCCESS_BOOK_APPOINTMENT_SCHEDULE,
  TRACKING_VISITED_SCHEDULE_APPOINTMENT,
} from '../OnboardingForm/constants';
import {
  addMonths,
  convertLocalBrowserDateToUserDate,
  getDateAvailability,
} from './utils';
import { AvailabilityEntry, ProviderTime } from '../../api/types';

export const useScheduleAppointmentPage = () => {
  const { page: pageView, track } = useTracking();
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const { userEmail } = state || {};
  const { userId: userIdQueryParam } = useReadUserIdFromQueryParams();
  const { mutation } = useScheduleAppointment();
  const [createAppointmentError, setCreateAppointmentError] = useState('');

  // `fetchStart` and `fetchEnd` define the time window for fetching
  // availability. On initialization, we fetch one month starting from "now".
  // Paging moves both ends onto the month being opened, so each month is asked
  // for once instead of re-requesting everything since today.
  const [fetchStart, setFetchStart] = useState<Date>(new Date());
  const [fetchEnd, setFetchEnd] = useState<Date>(addMonths(new Date(), 1));

  // This is the list of the availabilities (aka timeslots per day)
  // we have fetched so far from the backend.
  // When we fetch more, we append the new chunk to this already fetched list.
  const [availabilityRecords, setAvailabilityRecords] = useState<
    AvailabilityEntry[]
  >([]);

  // This is the date that the user has selected.
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  // This is the time within the selected date that the user has selected.
  const [selectedTime, setSelectedTime] = useState<
    string | ProviderTime | null
  >(null);

  const {
    availabilityResponse,
    availabilityType,
    isLoading: isFetchingAvailability,
    isStripe,
    referrer,
    timezone,
  } = useFetchAvailability({
    endDate: fetchEnd,
    startDate: fetchStart,
    userId: userIdQueryParam,
  });

  // The user's own referrer decides the cost copy, so it wins over any
  // utm_source in the URL, which useAffiliate falls back to. Arriving through
  // login means there is no utm_source at all.
  const { affiliate } = useAffiliate({ affiliateId: referrer });

  // Updates the availability records whenever we fetch a new chunk of data.
  useEffect(() => {
    if (isFetchingAvailability) return;
    if (!availabilityResponse) return;

    setAvailabilityRecords((s) => [...s, ...availabilityResponse.availability]);
  }, [availabilityResponse, isFetchingAvailability]);

  useEffect(() => {
    if (!userIdQueryParam) return;

    pageView(TRACKING_VISITED_SCHEDULE_APPOINTMENT);
  }, [pageView, userIdQueryParam]);

  const isInitiallyFetchingAvailability =
    isFetchingAvailability && !availabilityRecords?.length;

  // Held as a timestamp so the value keeps its identity across a refetch that
  // finds the same day. Consumers move the selection when it changes, and a
  // fresh Date on every fetch moved it on every fetch.
  const firstAvailableDayTime = useMemo<number>(() => {
    // find the first day having at least one time slot available
    const firstAvailability = availabilityRecords.find(
      (availability, _, allAvailabilities) => {
        // when creating a date starting from just a string, the browser by default reads
        // that as a UTC date and converts it into local timezone, but at this point our
        // availability date is already in the user timezone so we need to shift it accordingly
        const localBrowserDate = new Date(availability.date);
        const userTimezoneDate =
          convertLocalBrowserDateToUserDate(localBrowserDate);

        return !!getDateAvailability(allAvailabilities, userTimezoneDate)
          .length;
      }
    );

    // Start of day, or "no availability" would be a new value every render.
    if (!firstAvailability) return new Date().setHours(0, 0, 0, 0);

    // date in local browser timezone
    const firstAvailabilityDate = new Date(
      typeof firstAvailability.times[0] === 'string'
        ? firstAvailability.times[0]
        : firstAvailability.times[0]?.time
    );
    // conversion to the user timezone
    const firstAvailabilityConvertedToTimezone =
      firstAvailabilityDate.toLocaleString('en-US', {
        timeZone: timezone,
      });

    // recreate the date obj after the conversion
    return new Date(firstAvailabilityConvertedToTimezone).getTime();
  }, [availabilityRecords, timezone]);

  const firstAvailableDay = useMemo(
    () => new Date(firstAvailableDayTime),
    [firstAvailableDayTime]
  );

  // The grid shows a month at a time, so each month is fetched on its own. The
  // records already held are kept, so narrowing the window loses nothing. The
  // start never goes before today, there is nothing bookable back there.
  const onNavigateMonth = useCallback((monthStart: Date) => {
    const today = new Date();

    setFetchStart(monthStart > today ? monthStart : today);
    setFetchEnd(addMonths(monthStart, 1));
  }, []);

  useEffect(() => {
    setSelectedDate(firstAvailableDay);
  }, [firstAvailableDay]);

  const onSkip = useCallback(() => {
    track(TRACKING_SKIP_APPOINTMENT_SCHEDULE, { userId: userIdQueryParam });

    // meeting not booked,
    // skip feedback page and go straight to thank you
    navigate(`/thank-you${search}`, { state: { userEmail } });
  }, [navigate, search, track, userEmail, userIdQueryParam]);

  const onSelectDate = useCallback(
    (date: Date) => {
      track(TRACKING_SELECT_APPOINTMENT_DATE, { userId: userIdQueryParam });

      setSelectedDate(date);
    },
    [setSelectedDate, track, userIdQueryParam]
  );

  const onSelectTime = useCallback(
    (time: string | ProviderTime | null) => {
      track(TRACKING_SELECT_APPOINTMENT_TIME, { userId: userIdQueryParam });

      setSelectedTime(time);
    },
    [setSelectedTime, track, userIdQueryParam]
  );

  const onCreateMeeting = useCallback(async () => {
    track(TRACKING_CLICKED_CTA_TO_BOOK_APPOINTMENT, {
      userId: userIdQueryParam,
    });

    if (!selectedTime || !userIdQueryParam || !availabilityResponse) return;

    const selectedIsoString =
      typeof selectedTime === 'string' ? selectedTime : selectedTime.time;

    try {
      const bookedMeeting = await mutation.mutateAsync({
        meeting_type_id: availabilityResponse.meeting_type.id,
        meeting_date: selectedIsoString,
        user_id: userIdQueryParam,
        provider_id:
          typeof selectedTime === 'string'
            ? undefined
            : selectedTime.provider_id.toString(),
      });

      track(TRACKING_SUCCESS_BOOK_APPOINTMENT_SCHEDULE, {
        userId: userIdQueryParam,
      });

      // meeting has been booked,
      // so go to the feedback page
      navigate(`/feedback${search}`, {
        state: { userEmail, bookedMeeting },
      });
    } catch (error: any) {
      track(TRACKING_ERROR_ON_BOOK_APPOINTMENT, {
        error,
        userId: userIdQueryParam,
      });

      setCreateAppointmentError(
        typeof error?.message === 'string'
          ? error.message
          : 'An error occurred while creating the appointment. Please try again later.'
      );
    }
  }, [
    availabilityResponse,
    mutation,
    navigate,
    search,
    selectedTime,
    track,
    userEmail,
    userIdQueryParam,
  ]);

  const dismissCreateAppointmentError = useCallback(
    () => setCreateAppointmentError(''),
    [setCreateAppointmentError]
  );

  return {
    affiliate,
    availabilityRecords,
    availabilityType,
    createAppointmentError,
    dismissCreateAppointmentError,
    firstAvailableDay,
    isCreatingNewAppointment: mutation.isLoading,
    isFetchingAvailability,
    isInitiallyFetchingAvailability,
    isStripe,
    onCreateMeeting,
    onNavigateMonth,
    onSelectDate,
    onSelectTime,
    onSkip,
    selectedDate,
    selectedTime,
    userIdQueryParam,
    timezone,
  };
};
